import { useState } from "react";

import { useFormikContext } from "formik";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { SubmitButton } from "@/common/components/buttons/submit-button/SubmitButton";
import { FormShell } from "@/common/components/forms/FormShell";
import { TextField } from "@/common/components/forms/text-field/TextField";
import { useSubmitFirstField } from "@/common/components/forms/useSubmitFirstField";

import {
  SHAPEFILE_FARM_INITIAL_VALUES,
  shapefileFarmSchema,
  type ShapefileFarmFormValues,
} from "./farmImport.schema";
import { ShapeFileUpload, type ShapefileValidity } from "./ShapeFileUpload";
import {
  getShapefileValidationErrorMessageId,
  useImportShapefileMutation,
  useValidateShapefileMutation,
} from "./signup.api";

type Props = {
  countryCode: string;
};

const ShapefileFarmForm = ({ countryCode }: Props): React.ReactElement => {
  const navigate = useNavigate();
  const importShapefileMutation = useImportShapefileMutation();

  const handleSubmit = (values: ShapefileFarmFormValues) => {
    if (!values.shapeFile) {
      return;
    }

    importShapefileMutation.mutate(
      {
        countryCode,
        farmName: values.farmName,
        file: values.shapeFile,
      },
      { onSuccess: () => navigate("/farm") },
    );
  };

  return (
    <FormShell<ShapefileFarmFormValues>
      initialValues={SHAPEFILE_FARM_INITIAL_VALUES}
      onSubmit={handleSubmit}
      validationSchema={shapefileFarmSchema}
      footer={
        <SubmitButton
          data-testid="create-farm-submit"
          pending={importShapefileMutation.isPending}
        >
          <FormattedMessage id="common.loadAndEnter" />
        </SubmitButton>
      }
    >
      <TextField
        fullWidth
        label={<FormattedMessage id="common.farmName" />}
        name="farmName"
      />
      <ShapeFileField countryCode={countryCode} />
    </FormShell>
  );
};

const ShapeFileField = ({ countryCode }: Props): React.ReactElement => {
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const { setFieldValue } = useFormikContext<ShapefileFarmFormValues>();
  const { error } = useSubmitFirstField<File | null>("shapeFile");
  const validateShapefileMutation = useValidateShapefileMutation();

  const handleFileChange = (file: File | null) => {
    setPickedFile(file);

    if (!file) {
      validateShapefileMutation.reset();
      setFieldValue("shapeFile", null);
      return;
    }

    validateShapefileMutation.mutate(
      { countryCode, file },
      {
        onSuccess: () => setFieldValue("shapeFile", file),
        onError: () => setFieldValue("shapeFile", null),
      },
    );
  };

  const validity = toShapefileValidity(validateShapefileMutation);

  return (
    <ShapeFileUpload
      countryCode={countryCode}
      error={validity === "invalid" ? undefined : error}
      onFileChange={handleFileChange}
      validity={validity}
      value={pickedFile}
      invalidMessageId={
        validity === "invalid"
          ? getShapefileValidationErrorMessageId(
              validateShapefileMutation.error,
            )
          : undefined
      }
    />
  );
};

const toShapefileValidity = ({
  isError,
  isSuccess,
}: {
  isError: boolean;
  isSuccess: boolean;
}): ShapefileValidity => {
  if (isSuccess) {
    return "valid";
  }

  if (isError) {
    return "invalid";
  }

  return "unknown";
};

export { ShapefileFarmForm };
